/**
 * Opinionated publish theme presets (typography / chapter chrome overlays).
 * Applied via deep-ish merge onto an existing PublishSettings object.
 */

import type { PublishSettings } from "./publishFormattingShared";
import { defaultChapterDecoration } from "./publishFormattingShared";

export type PublishThemePreset = {
  id: string;
  name: string;
  description: string;
  /** Partial settings overrides merged onto the current PublishSettings. */
  partial: DeepPartialPublishSettings;
};

/** Loose partial for theme overlays — nested objects are shallow-merged per section. */
export type DeepPartialPublishSettings = {
  platform?: PublishSettings["platform"];
  trimSize?: PublishSettings["trimSize"];
  margins?: Partial<PublishSettings["margins"]>;
  typography?: Partial<PublishSettings["typography"]>;
  chapterSettings?: Partial<
    Omit<PublishSettings["chapterSettings"], "decoration">
  > & { decoration?: Partial<PublishSettings["chapterSettings"]["decoration"]> };
  toc?: Partial<PublishSettings["toc"]>;
  headerFooter?: Partial<PublishSettings["headerFooter"]>;
  frontMatter?: Partial<PublishSettings["frontMatter"]>;
  backMatter?: Partial<PublishSettings["backMatter"]>;
  sectionBreakOrnament?: string;
};

export const PUBLISH_THEME_PRESETS: PublishThemePreset[] = [
  {
    id: "classic-serif",
    name: "Classic Serif",
    description: "Traditional novel look — Garamond, centered headings, gentle ornament.",
    partial: {
      typography: {
        bodyFont: "EBGaramond",
        bodyFontSize: 12,
        lineSpacing: 1.15,
        paragraphStyle: "indent",
        firstLineIndent: 24,
        paragraphSpacing: 0,
      },
      chapterSettings: {
        headingStyle: "number-and-title",
        headingAlign: "center",
        headingFont: "same",
        numberFontSize: 22,
        titleFontSize: 16,
        dropCap: false,
        showDecoration: true,
        decoration: {
          ...defaultChapterDecoration(),
          kind: "ornament",
          ornament: "❧",
        },
        spaceBefore: 72,
        spaceAfter: 24,
      },
      headerFooter: {
        headerContent: "alternating",
        pageNumbers: "bottom-outer",
        suppressOnChapterFirstPage: true,
      },
      toc: { include: true, title: "Contents", style: "dot-leaders" },
      sectionBreakOrnament: "* * *",
    },
  },
  {
    id: "modern-clean",
    name: "Modern Clean",
    description: "Airy Baskerville, left-aligned titles, minimal chrome.",
    partial: {
      typography: {
        bodyFont: "LibreBaskerville",
        bodyFontSize: 11,
        lineSpacing: 1.5,
        paragraphStyle: "spacing",
        firstLineIndent: 0,
        paragraphSpacing: 10,
      },
      chapterSettings: {
        headingStyle: "title-only",
        headingAlign: "left",
        headingFont: "same",
        titleFontSize: 18,
        numberFontSize: 18,
        dropCap: false,
        showDecoration: false,
        spaceBefore: 48,
        spaceAfter: 28,
      },
      headerFooter: {
        headerContent: "none",
        pageNumbers: "bottom-center",
        suppressOnChapterFirstPage: true,
      },
      toc: { include: true, title: "Contents", style: "modern" },
      sectionBreakOrnament: "—",
    },
  },
  {
    id: "romance-soft",
    name: "Romance Soft",
    description: "Crimson Text with Playfair display titles and soft flourishes.",
    partial: {
      typography: {
        bodyFont: "CrimsonText",
        bodyFontSize: 12,
        lineSpacing: 1.3,
        paragraphStyle: "indent",
        firstLineIndent: 18,
        paragraphSpacing: 0,
      },
      chapterSettings: {
        headingStyle: "number-and-title",
        headingAlign: "center",
        headingFont: "PlayfairDisplay",
        decorationFont: "PlayfairDisplay",
        numberFontSize: 20,
        titleFontSize: 18,
        dropCap: true,
        showDecoration: true,
        decoration: {
          ...defaultChapterDecoration(),
          kind: "ornament",
          ornament: "✦ ✦ ✦",
        },
        spaceBefore: 64,
        spaceAfter: 28,
        captionItalic: true,
      },
      headerFooter: {
        headerContent: "chapter-title",
        pageNumbers: "bottom-center",
        suppressOnChapterFirstPage: true,
      },
      toc: { include: true, title: "Contents", style: "minimal" },
      sectionBreakOrnament: "✦ ✦ ✦",
    },
  },
  {
    id: "thriller-bold",
    name: "Thriller Bold",
    description: "Tight Liberation Serif, Cinzel chapter titles, stark rules.",
    partial: {
      typography: {
        bodyFont: "LiberationSerif",
        bodyFontSize: 11,
        lineSpacing: 1.15,
        paragraphStyle: "indent",
        firstLineIndent: 22,
        paragraphSpacing: 0,
      },
      chapterSettings: {
        headingStyle: "number-and-title",
        headingAlign: "left",
        headingFont: "Cinzel",
        decorationFont: "Cinzel",
        numberFontSize: 14,
        titleFontSize: 20,
        dropCap: false,
        showDecoration: true,
        decoration: {
          ...defaultChapterDecoration(),
          kind: "line",
          lineStyle: "thick",
        },
        spaceBefore: 56,
        spaceAfter: 20,
      },
      headerFooter: {
        headerContent: "book-title",
        pageNumbers: "bottom-outer",
        suppressOnChapterFirstPage: true,
      },
      toc: { include: true, title: "Contents", style: "dot-leaders" },
      sectionBreakOrnament: "◆ ◆ ◆",
    },
  },
];

export function getPublishThemePreset(themeId: string): PublishThemePreset | undefined {
  return PUBLISH_THEME_PRESETS.find((t) => t.id === themeId);
}

/** Apply a theme preset onto current settings (preserves platform / trim / margins unless overridden). */
export function applyPublishTheme(
  settings: PublishSettings,
  themeId: string
): PublishSettings {
  const preset = getPublishThemePreset(themeId);
  if (!preset) return settings;
  const p = preset.partial;
  return {
    ...settings,
    ...(p.platform ? { platform: p.platform } : {}),
    ...(p.trimSize ? { trimSize: p.trimSize } : {}),
    margins: { ...settings.margins, ...(p.margins ?? {}) },
    typography: { ...settings.typography, ...(p.typography ?? {}) },
    chapterSettings: {
      ...settings.chapterSettings,
      ...(p.chapterSettings ?? {}),
      decoration: {
        ...(settings.chapterSettings.decoration ?? defaultChapterDecoration()),
        ...(p.chapterSettings?.decoration ?? {}),
      },
    },
    toc: { ...settings.toc, ...(p.toc ?? {}) },
    headerFooter: { ...settings.headerFooter, ...(p.headerFooter ?? {}) },
    frontMatter: { ...settings.frontMatter, ...(p.frontMatter ?? {}) },
    backMatter: { ...settings.backMatter, ...(p.backMatter ?? {}) },
    ...(typeof p.sectionBreakOrnament === "string"
      ? { sectionBreakOrnament: p.sectionBreakOrnament }
      : {}),
  };
}
